import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { User, AuthContextType } from './types';

const AuthContext = createContext<AuthContextType | undefined>(undefined);
const BASE_URL = process.env.REACT_APP_BACKEND_URL.replace(/\/$/, '');

// Create a BroadcastChannel to communicate auth state changes between tabs of the same origin.
const authChannel = new BroadcastChannel('auth-channel');

export const useAuth = (): AuthContextType => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [user, setUser] = useState<User | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    // Modal State
    const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
    const [authModalView, setAuthModalView] = useState<'login' | 'register'>('login');

    const fetchUser = useCallback(async (isInitialLoad = false) => {
        if (isInitialLoad) setIsLoading(true);
        setError(null);
        try {
            const response = await fetch(`${BASE_URL}/api/users/me`, {
                credentials: 'include' // This is crucial for sending the httpOnly cookie
            });

            if (response.status === 401) {
                // Not logged in, which is a normal state.
                setUser(null);
                return;
            }
            if (!response.ok) throw new Error('Failed to verify session.');
            
            const userData = await response.json();
            setUser(userData);
        } catch (e) {
            // Network error or other issue, assume logged out
            setUser(null);
        } finally {
            if (isInitialLoad) setIsLoading(false);
        }
    }, []);

    // Initial session check on component mount
    useEffect(() => {
        fetchUser(true);
    }, [fetchUser]);

    // Re-check session on tab focus to sync login/logout state across different origins (e.g., app1 <-> app2)
    useEffect(() => {
        const handleFocus = () => fetchUser(false);
        window.addEventListener('focus', handleFocus);
        return () => window.removeEventListener('focus', handleFocus);
    }, [fetchUser]);

    // Listen for BroadcastChannel messages to sync state instantly between same-origin tabs
    useEffect(() => {
        const handleAuthChange = (event: MessageEvent) => {
            if (event.data && event.data.type === 'AUTH_STATE_CHANGED') {
                // Auth state changed in another tab, refetch user state
                fetchUser(false);
            }
        };

        authChannel.addEventListener('message', handleAuthChange);

        return () => {
            authChannel.removeEventListener('message', handleAuthChange);
        };
    }, [fetchUser]);


    const closeAuthModal = useCallback(() => {
        setIsAuthModalOpen(false);
        setError(null);
    }, []);

    // Auto-close modal on successful authentication
    useEffect(() => {
        if (user) {
            closeAuthModal();
        }
    }, [user, closeAuthModal]);

    const login = async (email: string, password: string): Promise<void> => {
        setIsLoading(true); setError(null);
        try {
            const response = await fetch(`${BASE_URL}/api/auth/login`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email, password }),
                credentials: 'include'
            });
            const data = await response.json();
            if (!response.ok) throw new Error(data.message || 'Login failed');
            setUser(data.user);
            // Broadcast the state change to other tabs
            authChannel.postMessage({ type: 'AUTH_STATE_CHANGED' });
        } catch (e) {
            setError(e instanceof Error ? e.message : 'An unknown error occurred.');
        } finally {
             setIsLoading(false);
        }
    };

    const register = async (name: string, email: string, password: string): Promise<void> => {
        setIsLoading(true); setError(null);
        try {
            const response = await fetch(`${BASE_URL}/api/auth/register`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ name, email, password }),
                credentials: 'include'
            });
            const data = await response.json();
            if (!response.ok) throw new Error(data.message || 'Registration failed');
            setUser(data.user);
            // Broadcast the state change to other tabs
            authChannel.postMessage({ type: 'AUTH_STATE_CHANGED' });
        } catch (e) {
            setError(e instanceof Error ? e.message : 'An unknown error occurred.');
        } finally {
            setIsLoading(false);
        }
    };

    const logout = async () => {
        setUser(null); // Optimistic update for instant UI feedback
        // Broadcast the state change immediately
        authChannel.postMessage({ type: 'AUTH_STATE_CHANGED' });
        try {
            await fetch(`${BASE_URL}/api/auth/logout`, {
                method: 'POST',
                credentials: 'include'
            });
        } catch (e) {
            console.error("Server logout failed:", e);
            // The user is already logged out on the frontend, and the change has been broadcast.
        }
    };
    
    const openAuthModal = useCallback((view: 'login' | 'register') => {
        setError(null);
        setAuthModalView(view);
        setIsAuthModalOpen(true);
    }, []);

    const value: AuthContextType = {
        user,
        isAuthenticated: !!user,
        isLoading,
        error,
        login,
        logout,
        register,
        isAuthModalOpen,
        authModalView,
        openAuthModal,
        closeAuthModal,
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};